/**
 * Sealed Inventory view - searchable / filterable table of sealed lots.
 */
import { DB } from "../lib/db";
import { openLabelPrint } from "../components/labelPrint";
import { slicePage, paginationControlsHtml } from "../utils/pagination";
import { sectionSort, sortRowsForView } from "../utils/table";
import { Lists } from "../lib/prefs";
import { escapeHtml, toast, $ } from "../lib/dom";
import { fmt$, fmt$0, fmtN, fmtDate, sum, kpiBar, todayISO, unitFMVForForm } from "../utils/format";
import { GAMES } from "../utils/pills";
import { onSectionChange, render } from "../state";
import { openModal, closeModal } from "../components/modal";
import { statusCellHtml } from "../components/statusCell";
import { openLotDrawer } from "../components/lotDrawer";
import { formField, readForm } from "../components/formField";
import { comboboxField, wireCombobox } from "../components/combobox";
import { nextId } from "../utils/ids";
import { SetCache } from "../lib/sets";
import { syncLotToSource } from "../lib/sync";
import { exportSealedReport, openExportOptions } from "../components/printReport";
import { openSealedPicker } from "../components/sealedProductPicker";
import { icon } from "../utils/icon";
import { downloadCsv } from "../lib/csv";
import { normalizeSetName } from "../lib/setNames";
import { basisProvenance } from "../lib/basisProvenance";
import { openEbaySoldsFor } from "../lib/ebaySolds";
import { bulkSetStatus } from "../components/statusCell";
import "../lib/generateFromInventory";
// Side-effect import: registers (window as any).openSealedPicker so the
// inline onclick="openSealedPicker()" handler below resolves at runtime.
void openSealedPicker;

/** Persisted across re-renders within the same session. Cleared on tab switch. */
export const SealedFilters = { search: "", game: "", productType: "", showZeroQty: false };

onSectionChange.push((_prev, _next) => { SealedFilters.search = ""; _selectedSealedLots.clear(); });

(window as any).SealedFilters = SealedFilters;

// Debounced search — see cards.ts twin for rationale.
let _sealedSearchTimer: ReturnType<typeof setTimeout> | null = null;
(window as any).sealedSearchInput = (v: string) => {
  SealedFilters.search = v;
  if (_sealedSearchTimer) clearTimeout(_sealedSearchTimer);
  _sealedSearchTimer = setTimeout(() => { _sealedSearchTimer = null; render(); }, 220);
};

/** data-col key -> raw sortable value; drives GLOBAL sorting before pagination. */
const SEALED_SORT_ACCESSORS: Record<string, (r: any) => any> = {
  "Lot ID":      r => r["Lot ID"],
  "Game":        r => r["Game"],
  "Product":     r => r["Product"],
  "Set":         r => r["Set"],
  "Type":        r => r["Product Type"],
  "Status":      r => r["Status"],
  "Qty":         r => Number(r["Qty"]) || 0,
  "Unit Basis":  r => Number(r["Unit Basis"]) || 0,
  "Total Basis": r => Number(r["Total Basis"]) || 0,
  "Unit FMV":    r => { const q = Number(r["Qty"]) || 0; const t = Number(r["Current FMV"]) || 0; return q > 0 ? t / q : t; },
  "Total FMV":   r => Number(r["Current FMV"]) || 0,
  "G/L":         r => (Number(r["Current FMV"]) || 0) - (Number(r["Total Basis"]) || 0),
  "Acq Date":    r => r["Acq Date"],
  "Source":      r => r["Source ID"],
};

/** Set of currently-checked sealed-lot IDs. Edit / Refresh / Lookup / Rip
 *  enable at size === 1; Delete / Generate Trade / Generate Sale at size >= 1. */
const _selectedSealedLots = new Set<string>();

/** Single-row click opens the lot detail drawer (click = open, checkbox =
 *  select). Edit lives inside the drawer. */
export function sealedRowClick(e: MouseEvent, lotId: string): void {
  const t = e.target as HTMLElement | null;
  if (t && t.closest("a, button, input, select, label")) return;
  openLotDrawer("sealed", lotId);
}
(window as any).sealedRowClick = sealedRowClick;

/** Checkbox change keeps row + Set in sync with the explicit checkbox. */
export function sealedRowToggle(lotId: string, checked: boolean): void {
  if (checked) _selectedSealedLots.add(lotId);
  else         _selectedSealedLots.delete(lotId);
  applySealedSelectionUI();
}
(window as any).sealedRowToggle = sealedRowToggle;

/** Header checkbox: toggle every visible sealed row. */
export function sealedSelectAll(checked: boolean): void {
  document.querySelectorAll<HTMLElement>("tr[data-sealed-row]").forEach(tr => {
    const id = tr.getAttribute("data-sealed-row") || "";
    if (!id) return;
    if (checked) _selectedSealedLots.add(id);
    else         _selectedSealedLots.delete(id);
  });
  applySealedSelectionUI();
}
(window as any).sealedSelectAll = sealedSelectAll;

function applySealedSelectionUI(): void {
  const size = _selectedSealedLots.size;
  document.querySelectorAll<HTMLElement>("tr[data-sealed-row]").forEach(tr => {
    const id = tr.getAttribute("data-sealed-row") || "";
    const sel = _selectedSealedLots.has(id);
    tr.setAttribute("data-selected", String(sel));
    const cb = tr.querySelector<HTMLInputElement>('input[type="checkbox"][data-sealed-row-cb]');
    if (cb) cb.checked = sel;
  });
  // Single-lot actions: enabled only at exactly one selection. Rip is in this
  // group because each rip event is per-sealed-product.
  ["sealedActRefresh", "sealedActLookup", "sealedActEbay", "sealedActRip"].forEach(id => {
    const btn = document.getElementById(id) as HTMLButtonElement | null;
    if (btn) btn.disabled = size !== 1;
  });
  // Bulk actions: enabled at >= 1.
  ["sealedActDelete", "sealedActTrade", "sealedActSale", "sealedActMarkListed", "sealedActMarkAvail", "sealedActLabels"].forEach(id => {
    const btn = document.getElementById(id) as HTMLButtonElement | null;
    if (btn) btn.disabled = size < 1;
  });
  const lbl = document.getElementById("sealedSelLabel");
  if (lbl) {
    if (size === 0)      lbl.textContent = "";
    else if (size === 1) lbl.textContent = Array.from(_selectedSealedLots)[0]!;
    else                 lbl.textContent = `${size} selected`;
  }
  const bar = document.getElementById("sealedSelectionBar");
  if (bar) bar.style.display = size === 0 ? "none" : "flex";
  const sa = document.getElementById("sealedSelectAllCb") as HTMLInputElement | null;
  if (sa) {
    const rows = document.querySelectorAll<HTMLElement>("tr[data-sealed-row]");
    const all = rows.length > 0 && Array.from(rows).every(r => _selectedSealedLots.has(r.getAttribute("data-sealed-row") || ""));
    sa.checked = all;
    sa.indeterminate = !all && size > 0;
  }
}

function _singleSelectedSealed(): string | null {
  return _selectedSealedLots.size === 1 ? Array.from(_selectedSealedLots)[0]! : null;
}

function sealedToolbarEdit():    void { const id = _singleSelectedSealed(); if (id) editSealed(id); }
function sealedToolbarLabels():  void { openLabelPrint("sealed", Array.from(_selectedSealedLots)); }
function sealedToolbarRefresh(): void {
  const id = _singleSelectedSealed(); if (!id) return;
  const fn = (window as any).refreshSealedFMV;
  if (typeof fn === "function") fn(id);
}
function sealedToolbarLookup():  void { const id = _singleSelectedSealed(); if (id) lookupSealedPrice(id); }
function sealedToolbarEbay():    void { const id = _singleSelectedSealed(); if (id) openEbaySoldsFor("sealed", id); }
function sealedToolbarRip():     void {
  const id = _singleSelectedSealed(); if (!id) return;
  const fn = (window as any).startRip;
  if (typeof fn === "function") fn(id);
}
function sealedToolbarDelete():  void {
  if (_selectedSealedLots.size === 0) return;
  const ids = Array.from(_selectedSealedLots);
  if (ids.length > 1 && !confirm(`Delete ${ids.length} sealed lots? This can't be undone.`)) return;
  const fn = (window as any).deleteRow;
  if (typeof fn !== "function") return;
  for (const id of ids) fn("sealed", "Lot ID", id);
  _selectedSealedLots.clear();
  applySealedSelectionUI();
}
function sealedToolbarTrade(): void {
  if (_selectedSealedLots.size === 0) return;
  const fn = (window as any).generateTradeFromLots;
  if (typeof fn === "function") fn(Array.from(_selectedSealedLots), "sealed");
}
function sealedToolbarSale(): void {
  if (_selectedSealedLots.size === 0) return;
  const fn = (window as any).generateSaleFromLots;
  if (typeof fn === "function") fn(Array.from(_selectedSealedLots), "sealed");
}
function sealedToolbarMark(value: string): void {
  if (_selectedSealedLots.size === 0) return;
  bulkSetStatus("sealed", Array.from(_selectedSealedLots), value);
}
(window as any).sealedToolbarMark    = sealedToolbarMark;
(window as any).sealedToolbarEdit    = sealedToolbarEdit;
(window as any).sealedToolbarRefresh = sealedToolbarRefresh;
(window as any).sealedToolbarLookup  = sealedToolbarLookup;
(window as any).sealedToolbarEbay    = sealedToolbarEbay;
(window as any).sealedToolbarRip     = sealedToolbarRip;
(window as any).sealedToolbarTrade   = sealedToolbarTrade;
(window as any).sealedToolbarSale    = sealedToolbarSale;
(window as any).sealedToolbarDelete  = sealedToolbarDelete;

/** Opens the Export Options modal, then re-applies the viewSealed() filter chain
 *  and hands the chosen format/detail off to the report renderer.
 */
export function exportSealedReportFromView(): void {
  openExportOptions("Sealed Inventory", (format, detail) => {
    const f = SealedFilters;
    let rows = DB.table("sealed").slice();
    if (f.search) {
      const q = f.search.toLowerCase();
      rows = rows.filter(r => Object.values(r).some(v => String(v || "").toLowerCase().includes(q)));
    }
    if (f.game)           rows = rows.filter(r => (r["Game"] || "") === f.game);
    if (f.productType)    rows = rows.filter(r => r["Product Type"] === f.productType);
    if (!f.showZeroQty)   rows = rows.filter(r => (Number(r["Qty"]) || 0) > 0);
    exportSealedReport(format, detail, rows, f);
  });
}
(window as any).exportSealedReportFromView = exportSealedReportFromView;

/** Download the currently-filtered (and sorted) sealed lots as a raw-value CSV. */
export function exportSealedCsv(): void {
  const f = SealedFilters;
  let rows = DB.table("sealed").slice();
  if (f.search) {
    const q = f.search.toLowerCase();
    rows = rows.filter(r => Object.values(r).some(v => String(v || "").toLowerCase().includes(q)));
  }
  if (f.game)        rows = rows.filter(r => (r["Game"] || "") === f.game);
  if (f.productType) rows = rows.filter(r => r["Product Type"] === f.productType);
  if (!f.showZeroQty) rows = rows.filter(r => (Number(r["Qty"]) || 0) > 0);
  const sort = sectionSort("sealed");
  if (sort && SEALED_SORT_ACCESSORS[sort.key]) rows = sortRowsForView(rows, sort.dir, SEALED_SORT_ACCESSORS[sort.key]!);
  const unit = (r: any) => { const q = Number(r["Qty"]) || 0; const t = Number(r["Current FMV"]) || 0; return q > 0 ? +(t / q).toFixed(2) : t; };
  downloadCsv(`sealed-inventory-${todayISO()}.csv`, rows, [
    { label: "Lot ID",       get: r => r["Lot ID"] },
    { label: "Game",         get: r => r["Game"] },
    { label: "Product",      get: r => r["Product"] },
    { label: "Set",          get: r => r["Set"] },
    { label: "Product Type", get: r => r["Product Type"] },
    { label: "Status",       get: r => r["Status"] },
    { label: "Qty",          get: r => r["Qty"] },
    { label: "Unit Basis",   get: r => r["Unit Basis"] },
    { label: "Total Basis",  get: r => r["Total Basis"] },
    { label: "Unit FMV",     get: unit },
    { label: "Total FMV",    get: r => r["Current FMV"] },
    { label: "G/L",          get: r => +(((Number(r["Current FMV"]) || 0) - (Number(r["Total Basis"]) || 0))).toFixed(2) },
    { label: "Acq Date",     get: r => fmtDate(r["Acq Date"]) },
    { label: "Acq Type",     get: r => r["Acq Type"] },
    { label: "Source ID",    get: r => r["Source ID"] },
    { label: "Notes",        get: r => r["Notes"] },
  ]);
  toast(`CSV downloaded \u00b7 ${rows.length} row(s)`);
}
(window as any).exportSealedCsv = exportSealedCsv;

export function viewSealed(root: HTMLElement): void {
  const f = SealedFilters;
  const all = DB.table("sealed");
  let rows = all.slice();
  if (f.search) {
    const q = f.search.toLowerCase();
    rows = rows.filter(r => Object.values(r).some(v => String(v || "").toLowerCase().includes(q)));
  }
  if (f.game)           rows = rows.filter(r => (r["Game"] || "") === f.game);
  if (f.productType)    rows = rows.filter(r => r["Product Type"] === f.productType);
  // Hide lots with no stock left unless the user opts in via the toggle.
  // Empty lots stay in the database (we still need their history) but they
  // clutter the active-inventory view.
  if (!f.showZeroQty)   rows = rows.filter(r => (Number(r["Qty"]) || 0) > 0);

  // Global sort: order the FULL filtered set before pagination.
  const sort = sectionSort("sealed");
  if (sort && SEALED_SORT_ACCESSORS[sort.key]) rows = sortRowsForView(rows, sort.dir, SEALED_SORT_ACCESSORS[sort.key]!);

  const totalQty   = sum(rows, r => r["Qty"]);
  const totalBasis = sum(rows, r => r["Total Basis"]);
  const totalFMV   = sum(rows, r => r["Current FMV"]);
  const unrlz      = totalFMV - totalBasis;

  const rows_total = rows.length;
  const rows_page = slicePage("sealed", rows, 100);
  root.innerHTML = `
    ${kpiBar([
      { label: "Lots shown", value: fmtN(rows.length) },
      { label: "Units",      value: fmtN(totalQty) },
      { label: "Cost Basis", value: fmt$0(totalBasis) },
      { label: "FMV",        value: fmt$0(totalFMV), tone: unrlz >= 0 ? "green" : "red" },
      { label: "Unrealized", value: (unrlz >= 0 ? "+" : "\u2212") + fmt$0(Math.abs(unrlz)), tone: unrlz >= 0 ? "green" : "red" },
    ])}

    <div class="panel p-3 mb-3 flex flex-col gap-2">
      <!-- Row 1: search + filter dropdowns + clear + export -->
      <div class="flex flex-wrap gap-2 items-center">
        <div class="relative flex-1 min-w-[180px]">
          <input class="input w-full pr-8"
                 placeholder="Search product, set, lot ID, notes..."
                 value="${escapeHtml(f.search)}"
                 oninput="sealedSearchInput(this.value)"
                 data-focus-key="sealed-search">
          ${f.search ? `<button type="button" class="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-white text-sm leading-none" onmousedown="event.preventDefault()" onclick="SealedFilters.search='';render()" title="Clear search">&#x2715;</button>` : ""}
        </div>
        <select class="select !w-auto" title="Filter by game" onchange="SealedFilters.game=this.value;render()">
          <option value="">All games</option>
          ${GAMES.map(o => `<option ${f.game === o ? "selected" : ""}>${escapeHtml(o)}</option>`).join("")}
        </select>
        <select class="select !w-auto" title="Filter by product type" onchange="SealedFilters.productType=this.value;render()">
          <option value="">All types</option>
          ${Lists.get("Product Type").map(o => `<option ${f.productType === o ? "selected" : ""}>${escapeHtml(o)}</option>`).join("")}
        </select>
        <label class="text-xs flex items-center gap-1 cursor-pointer select-none whitespace-nowrap" title="Show lots that have been fully sold, traded, ripped, or gifted (Qty = 0)">
          <input type="checkbox" onchange="SealedFilters.showZeroQty=this.checked;render()" ${f.showZeroQty ? "checked" : ""}>
          Show empty
        </label>
        ${(f.search || f.game || f.productType || f.showZeroQty)
          ? `<button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="SealedFilters.search='';SealedFilters.game='';SealedFilters.productType='';SealedFilters.showZeroQty=false;render()">Clear</button>`
          : ""}
        <button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="exportSealedReportFromView()" title="Export currently-visible rows as a printable PDF report">${icon("printer")} Export</button>
        <button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="exportSealedCsv()" title="Download the currently-filtered rows as a CSV (raw values, Excel/Sheets-ready)">${icon("download")} CSV</button>
        <div data-cols-host></div>
      </div>
      <!-- Selection bar — appears only when rows are checked. Refresh/Lookup/Rip
           need exactly one checked row; the rest work on any number. -->
      <div id="sealedSelectionBar" class="flex flex-wrap gap-2 items-center pt-2 border-t border-[var(--border)]" style="display:none">
        <span class="text-xs text-[var(--muted)]">Selected: <code id="sealedSelLabel"></code></span>
        <div class="ml-auto flex gap-1 flex-wrap">
          <button id="sealedActRefresh" class="btn btn-ghost !py-1 !px-2 text-xs" disabled onclick="sealedToolbarRefresh()" title="Refresh FMV from market (one row only)">${icon("refresh")} Refresh FMV</button>
          <button id="sealedActLookup"  class="btn btn-ghost !py-1 !px-2 text-xs" disabled onclick="sealedToolbarLookup()"  title="Search this product on TCGplayer (one row only)">${icon("search")} Lookup</button>
          <button id="sealedActEbay"    class="btn btn-ghost !py-1 !px-2 text-xs" disabled onclick="sealedToolbarEbay()"    title="Open eBay SOLD listings for this product, newest first — real recent sale prices (one row only)">${icon("dollar-sign")} eBay Solds</button>
          <button id="sealedActMarkListed" class="btn btn-ghost !py-1 !px-2 text-xs" disabled onclick="sealedToolbarMark('Listed')" title="Mark every selected Available/Listed lot as Listed (machine-managed statuses are skipped)">${icon("tag")} Mark Listed</button>
          <button id="sealedActMarkAvail"  class="btn btn-ghost !py-1 !px-2 text-xs" disabled onclick="sealedToolbarMark('Available')" title="Mark every selected Available/Listed lot as Available">${icon("check-circle")} Mark Available</button>
          <button id="sealedActLabels" class="btn btn-ghost !py-1 !px-2 text-xs" disabled onclick="sealedToolbarLabels()" title="Print QR labels for the selected lots (label-writer friendly)">${icon("tag")} Labels</button>
          <button id="sealedActTrade"   class="btn btn-ghost !py-1 !px-2 text-xs" disabled onclick="sealedToolbarTrade()"   title="Open a new trade with the selected lots as outgoing items">${icon("route")} Generate Trade</button>
          <button id="sealedActSale"    class="btn btn-ghost !py-1 !px-2 text-xs" disabled onclick="sealedToolbarSale()"    title="Open a new sale with the selected lots as items">${icon("wallet")} Generate Sale</button>
          <button id="sealedActRip"     class="btn btn-ghost !py-1 !px-2 text-xs" disabled onclick="sealedToolbarRip()"     title="Open / rip this sealed product (one row only)">${icon("scissors")} Generate Rip</button>
          <button id="sealedActDelete"  class="btn btn-ghost !py-1 !px-2 text-xs !text-rose-300" disabled onclick="sealedToolbarDelete()" title="Delete selected sealed lots (any number)">${icon("trash")} Delete</button>
        </div>
      </div>
    </div>

    <div class="panel scrollx">
      <table class="t">
        <thead><tr>
          <th class="!w-8"><input type="checkbox" id="sealedSelectAllCb" onclick="sealedSelectAll(this.checked)" title="Select / deselect all visible rows"></th><th></th><th data-col="Lot ID">Lot ID</th><th data-col="Game">Game</th><th data-col="Product" class="cell-clip">Product</th><th data-col="Set" class="cell-clip">Set</th><th data-col="Type">Type</th><th data-col="Status">Status</th>
          <th data-col="Qty" class="text-right">Qty</th><th data-col="Unit Basis" class="text-right">Unit Basis</th><th data-col="Total Basis" class="text-right">Total Basis</th>
          <th data-col="Unit FMV" class="text-right" title="Market value per single unit — what you'd ask for one">Unit FMV</th><th data-col="Total FMV" class="text-right">Total FMV</th><th data-col="G/L" class="text-right">G/L</th><th data-col="Acq Date">Acq Date</th>
          <th data-col="Source" class="cell-clip">Source</th>
        </tr></thead>
        <tbody>
          ${rows_page.map(r => {
            const totalFMVrow = Number(r["Current FMV"]) || 0;
            const qty         = Number(r["Qty"]) || 0;
            const unitFMV     = qty > 0 ? totalFMVrow / qty : totalFMVrow;
            const gl          = totalFMVrow - (Number(r["Total Basis"]) || 0);
            const lotId       = escapeHtml(r["Lot ID"]);
            const srcId       = r["Source ID"] ? escapeHtml(r["Source ID"]) : "";
            const imgUrl      = r["Image URL"] || "";
            const isSel       = _selectedSealedLots.has(String(r["Lot ID"]));
            return `<tr data-sealed-row="${lotId}" data-selectable data-selected="${isSel}"
                        onclick="sealedRowClick(event, '${lotId}')">
              <td class="text-center !w-8"><input type="checkbox" data-sealed-row-cb ${isSel ? "checked" : ""} onclick="sealedRowToggle('${lotId}', this.checked); event.stopPropagation()"></td>
              <td class="text-center">${imgUrl ? `<img src="${escapeHtml(imgUrl)}" loading="lazy" class="inline-block rounded thumb-hover" style="height:40px; width:auto; max-width:50px; object-fit:contain" alt="">` : ""}</td>
              <td class="font-mono text-xs"><a class="text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${lotId}')">${lotId}</a></td>
              <td class="text-xs text-[var(--muted)]">${escapeHtml(r["Game"] || "-")}</td>
              <td class="cell-clip">${escapeHtml(r["Product"] || "")}</td>
              <td class="cell-clip">${escapeHtml(r["Set"] || "")}</td>
              <td class="text-xs text-[var(--muted)]">${escapeHtml(r["Product Type"] || "")}</td>
              <td>${statusCellHtml("sealed", String(r["Lot ID"] || ""), String(r["Status"] || "Available"))}</td>
              <td class="text-right stat-num">${fmtN(r["Qty"])}</td>
              <td class="text-right stat-num">${fmt$(r["Unit Basis"])}</td>
              <td class="text-right stat-num">${fmt$(r["Total Basis"])}</td>
              <td class="text-right stat-num">${r["FMV Locked"] ? `<span title="FMV locked (manual) \u2014 auto-refresh skips this lot">${icon("lock", { size: 12 })}</span> ` : ""}${fmt$(unitFMV)}</td>
              <td class="text-right stat-num">${fmt$(totalFMVrow)}</td>
              <td class="text-right stat-num ${gl >= 0 ? "text-emerald-300" : "text-rose-300"}">${fmt$(gl)}</td>
              <td class="text-xs text-[var(--muted)]">${escapeHtml(fmtDate(r["Acq Date"]))}</td>
              <td class="font-mono text-xs text-[var(--muted)] cell-clip">${srcId ? `<a class="text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${srcId}')">${srcId}</a>` : ""}</td>
            </tr>`;
          }).join("") || `<tr><td colspan="16" class="text-center text-[var(--muted)] py-10">
              <div class="mb-3">No sealed lots match your filters.</div>
              <button class="btn btn-primary !py-1.5 !px-3 text-sm" onclick="editSealed()">${icon("plus")} Add Sealed</button>
            </td></tr>`}
        </tbody>
      </table>
    </div>
    ${paginationControlsHtml("sealed", rows_total, 100)}
  `;
  applySealedSelectionUI();
}

(window as any).render = render;

/** Shared Status list + the row's current value appended when it's a legacy
 *  value no longer in the list — so opening the edit form never silently
 *  blanks the field. */
function statusOptionsFor(currentValue: string | undefined): string[] {
  const list = Lists.get("Status");
  if (currentValue && !list.includes(currentValue)) return [...list, currentValue];
  return list;
}

/** Same legacy-value protection for Product Type. */
function productTypeOptionsFor(currentValue: string | undefined): string[] {
  const list = Lists.get("Product Type");
  if (currentValue && !list.includes(currentValue)) return [...list, currentValue];
  return list;
}

/* ============================================================
   Sealed lot form (editSealed / saveSealed)
   ============================================================ */
export function editSealed(id?: string): void {
  const isNew = !id;
  const row = isNew ? {
    "Lot ID": nextId("S", "sealed", "Lot ID"),
    "Acq Date": todayISO(),
    "Acq Type": "Purchase",
    "Game": "Pokemon",
    "Status": "Available",
    "Qty": 1,
  } : DB.table("sealed").find(r => r["Lot ID"] === id);
  if (!row) { toast("Lot not found", "err"); return; }

  const sourceOpts = [
    ...DB.table("purchases").map(p => p["Purchase ID"]).filter(Boolean),
    ...DB.table("transactions").map(t => t["Txn ID"]).filter(Boolean),
  ];

  const body = `
    <form id="sealedForm" class="grid grid-cols-1 sm:grid-cols-2 gap-3" onsubmit="return false;">
      ${formField({ label: "Lot ID", name: "Lot ID", value: row["Lot ID"], required: true })}
      ${formField({ label: "Acq Date", name: "Acq Date", type: "date", value: fmtDate(row["Acq Date"]), max: todayISO() })}
      ${formField({ label: "Game", name: "Game", value: row["Game"] || "Pokemon", options: GAMES, required: true })}
      ${comboboxField({ label: "Set", name: "Set", value: row["Set"] || "", placeholder: "Type to search sets...", hint: "Auto-loads sets for the selected game (cached locally)." })}
      <div class="sm:col-span-2">
        <label class="label" for="f_Product">Product <span class="text-red-400">*</span></label>
        <div class="flex gap-2">
          <input class="input flex-1" id="f_Product" name="Product" value="${escapeHtml(row["Product"] || "")}" required placeholder="e.g. 'Surging Sparks Booster Box'">
          <button type="button" class="btn btn-ghost whitespace-nowrap" onclick="openSealedPicker()" title="Search the TCGCSV catalog by name; picks fill Product, Set, Image URL, and FMV in one click.">${icon("search")} Browse catalog</button>
        </div>
        <div class="text-xs text-[var(--muted)] mt-1">${row["TCG Product ID"] ? `Linked to TCGCSV product <code>#${escapeHtml(row["TCG Product ID"])}</code> - Refresh FMV uses the live price.` : `Click Browse to link this lot to the TCGCSV catalog and unlock auto-FMV refresh + price history.`}</div>
        <input type="hidden" name="TCG Product ID" value="${escapeHtml(row["TCG Product ID"] || "")}">
      </div>
      ${formField({ label: "Product Type", name: "Product Type", value: row["Product Type"], options: productTypeOptionsFor(row["Product Type"]) })}
      ${formField({ label: "Status", name: "Status", value: row["Status"] || "Available", options: statusOptionsFor(row["Status"] || "Available") })}
      ${formField({ label: "Acq Type", name: "Acq Type", value: row["Acq Type"], options: Lists.get("Acq Type") })}
      ${formField({ label: "Qty", name: "Qty", type: "number", step: "1", value: row["Qty"] })}
      <div>
        <label class="label" for="f_Unit_Basis">Unit Basis ($)</label>
        <div class="flex gap-2">
          <input class="input flex-1" id="f_Unit_Basis" name="Unit Basis" type="number" step="0.01" value="${row["Unit Basis"] ?? ""}" ${basisProvenance(row["Source ID"]) ? "readonly" : ""}>
          ${(() => { const bp = basisProvenance(row["Source ID"]); return bp && bp.overridable ? `<button type="button" class="btn btn-ghost whitespace-nowrap" id="basisOverrideBtn" title="Unlock the basis for a deliberate manual correction">${icon("edit")} Override</button>` : ""; })()}
        </div>
        ${(() => {
          const bp = basisProvenance(row["Source ID"]);
          const src = escapeHtml(String(row["Source ID"] || ""));
          if (!bp) return `<div class="text-xs text-[var(--muted)] mt-1">Cost per single unit</div>`;
          return `<div class="text-xs text-[var(--muted)] mt-1">Calculated by ${bp.kind} <a class="font-mono text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${src}')">${src}</a> \u2014 ${bp.how}.${bp.overridable ? " Prefer editing the source transaction; Override is for deliberate corrections only." : ""}</div>`;
        })()}
      </div>
      <div>
        <label class="label" for="f_Current_FMV">Unit FMV ($)</label>
        <div class="flex gap-2">
          <input class="input flex-1" id="f_Current_FMV" name="Current FMV" type="number" step="0.01" value="${unitFMVForForm(row)}">
          <button type="button" class="btn btn-ghost whitespace-nowrap" onclick="refreshFMVInForm('sealed')" title="Look up market FMV per unit">${icon("refresh")} Lookup</button>
        </div>
        <label class="flex items-center gap-2 text-xs mt-2 cursor-pointer select-none" title="When locked, the daily FMV auto-sweep and Refresh-all never overwrite this lot's FMV. Manual price entry locks automatically.">
          <input type="checkbox" name="FMV Locked" ${row["FMV Locked"] ? "checked" : ""}> ${icon("lock", { size: 13 })} Lock FMV (protect from auto-refresh)
        </label>
        <div class="text-xs text-[var(--muted)] mt-1">Total Basis and Total FMV are computed automatically (x Qty) and shown on the main table.</div>
      </div>
      <div>
        <label class="label">Price Source</label>
        <div class="text-sm py-2" id="sealedPriceSource"></div>
      </div>
      ${formField({ label: "Source ID", name: "Source ID", value: row["Source ID"], options: sourceOpts, hint: "Link to a purchase (PUR-...) or adjustment (TXN-...)" })}
      <div class="sm:col-span-2">
        <label class="label" for="f_Sealed_Image_URL">Image URL</label>
        <div class="flex gap-2 items-start">
          <input class="input flex-1" id="f_Sealed_Image_URL" name="Image URL" value="${escapeHtml(row["Image URL"] || "")}" placeholder="Paste a product image URL (TCGplayer, manufacturer page, etc.)" oninput="window._sealedThumbPreview && window._sealedThumbPreview(this.value)">
          <img id="sealedThumbPreview" src="${escapeHtml(row["Image URL"] || "")}" class="${row["Image URL"] ? "" : "hidden"} rounded border border-[var(--border)] shrink-0" style="height:80px; width:auto; max-width:80px; object-fit:contain" alt="">
        </div>
        <div class="text-xs text-[var(--muted)] mt-1">Filled automatically when you link via Browse catalog. Leave blank for no thumbnail.</div>
      </div>
      <div class="sm:col-span-2">${formField({ label: "Notes", name: "Notes", type: "textarea", value: row["Notes"] })}</div>
    </form>`;
  const safeId = escapeHtml(row["Lot ID"]);
  const footer = `
    ${!isNew ? `<button class="btn btn-danger" onclick="deleteRow('sealed','Lot ID','${safeId}')">Delete</button>` : ""}
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="saveSealed('${isNew ? "" : safeId}')">${isNew ? "Add" : "Save"}</button>`;
  openModal({ title: isNew ? "Add Sealed Lot" : "Edit Sealed Lot", body, footer, wide: true });

  (window as any)._sealedThumbPreview = (url: string) => {
    const img = document.getElementById("sealedThumbPreview") as HTMLImageElement | null;
    if (!img) return;
    if (url && url.trim()) { img.src = url.trim(); img.classList.remove("hidden"); }
    else { img.removeAttribute("src"); img.classList.add("hidden"); }
  };

  // Derived price-source indicator — replaces the old Valuation Source
  // dropdown. Computed from catalog link + lock state; updates on lock toggle.
  const updateSealedPriceSource = () => {
    const el = document.getElementById("sealedPriceSource");
    if (!el) return;
    const locked = !!document.querySelector<HTMLInputElement>('#sealedForm [name="FMV Locked"]')?.checked;
    const pid = (document.querySelector<HTMLInputElement>('#sealedForm [name="TCG Product ID"]')?.value || "").trim();
    el.innerHTML = locked ? `${icon("lock", { size: 13 })} Manual \u2014 locked; auto-repricing skips this lot`
      : pid ? `${icon("link", { size: 13 })} Catalog-linked \u2014 repriced nightly`
      : `${icon("edit", { size: 13 })} Unlinked \u2014 manual price; use ${icon("search", { size: 13 })} Browse catalog to enable auto-repricing`;
  };
  updateSealedPriceSource();
  document.querySelector<HTMLInputElement>('#sealedForm [name="FMV Locked"]')?.addEventListener("change", updateSealedPriceSource);

  // Derived-basis soft lock (see cards.ts twin).
  document.getElementById("basisOverrideBtn")?.addEventListener("click", () => {
    const src = String(row["Source ID"] || "");
    if (!confirm(`This basis was calculated from ${src}. Overriding disconnects this lot from its source transaction's totals \u2014 Books Health will flag the difference, and future sales will use YOUR number for COGS.\n\nOverride the basis?`)) return;
    const inp = document.getElementById("f_Unit_Basis") as HTMLInputElement | null;
    if (inp) { inp.readOnly = false; inp.focus(); inp.select(); }
    document.getElementById("basisOverrideBtn")?.remove();
  });

  const form = $("#sealedForm") as HTMLFormElement;
  const gameSel = form.querySelector<HTMLSelectElement>('[name="Game"]')!;
  const setWrapper = form.querySelector<HTMLElement>('.combobox-wrapper[data-cbx="Set"]')!;
  let currentGame = gameSel.value || "Pokemon";

  const cbx = wireCombobox(setWrapper, {
    getOptions: () => SetCache.get(currentGame),
    asyncLoader: () => SetCache.ensureFresh(currentGame).then(() => cbx.refresh()),
  });

  gameSel.addEventListener("change", () => {
    currentGame = gameSel.value || "Pokemon";
    setWrapper.querySelector<HTMLInputElement>("input")!.value = "";
    (setWrapper.querySelector<HTMLElement>(".combobox-list")!).style.display = "none";
    SetCache.ensureFresh(currentGame);
  });
}

export function saveSealed(origId: string): void {
  const data = readForm($("#sealedForm"));
  data["Set"] = normalizeSetName(data["Set"]);
  const qty       = Number(data["Qty"])         || 0;
  const unitBasis = Number(data["Unit Basis"])  || 0;
  const unitFMV   = Number(data["Current FMV"]) || 0;
  data["Total Basis"]    = +(unitBasis * qty).toFixed(2);
  data["Current FMV"]    = +(unitFMV   * qty).toFixed(2);
  data["Unrealized G/L"] = +(data["Current FMV"] - data["Total Basis"]).toFixed(2);
  if (origId) DB.update("sealed", "Lot ID", origId, data);
  else        DB.add("sealed", data);
  syncLotToSource(data["Lot ID"], "Sealed", data);
  toast("Sealed lot saved");
  closeModal();
  render();
}

/** Lookup button on a sealed row - opens TCGplayer search in a new tab. */
export function lookupSealedPrice(id: string): void {
  const r = DB.table("sealed").find(s => s["Lot ID"] === id);
  if (!r) return;
  const q = [r["Product"] || "", r["Set"] || ""].filter(Boolean).join(" ").trim();
  if (!q) { toast("No product info.", "err"); return; }
  window.open("https://www.tcgplayer.com/search/all/product?q=" + encodeURIComponent(q), "_blank", "noopener");
}

// Inline-handler bridges
(window as any).editSealed         = editSealed;
(window as any).saveSealed         = saveSealed;
(window as any).lookupSealedPrice  = lookupSealedPrice;
(window as any).sealedToolbarLabels = sealedToolbarLabels;
